import type { CmmdRuntimeInventoryRuntime } from "@cmmd-forge/contracts";

import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import { cmmdRuntimeInventoryResource } from "./cmmdRuntimeInventoryResource";

export type RuntimeAvailability = "available" | "offline";

/**
 * "unknown" is a runtime the inventory does not list at all. It is deliberately
 * not "offline": a runtime CMMD has never heard of cannot hold a lifecycle
 * intent, so the caller must keep the ordinary connect path and its existing
 * runtime-unavailable failure.
 */
export type RuntimeAvailabilityForEnvironment = RuntimeAvailability | "unknown";

/** The one offline predicate; the thread route and the settle path must agree. */
export function runtimeAvailability(runtime: CmmdRuntimeInventoryRuntime): RuntimeAvailability {
  return runtime.inventoryStatus === "offline" || runtime.runtime.status === "offline"
    ? "offline"
    : "available";
}

/**
 * A legacy cloud environment id carries the runtime id behind a prefix; a
 * desktop id is the runtime id. A durable `env_` id names no runtime id at
 * all, so it falls through unchanged, same as a desktop id.
 */
export function resolveRuntimeIdForEnvironment(environmentId: string): string {
  return classifyEnvironmentId(environmentId).legacyRuntimeId ?? environmentId;
}

function findInventoryRuntimeForEnvironment(
  environmentId: string,
  runtimes: readonly CmmdRuntimeInventoryRuntime[],
): CmmdRuntimeInventoryRuntime | undefined {
  const cloudRuntimeId = classifyEnvironmentId(environmentId).legacyRuntimeId;
  const runtimeKind = cloudRuntimeId === null ? "desktop-local" : "cloud-vm";
  const runtimeId = cloudRuntimeId ?? environmentId;
  return runtimes.find(
    (candidate) =>
      candidate.runtime.runtimeKind === runtimeKind && candidate.runtime.id === runtimeId,
  );
}

export function resolveRuntimeAvailabilityForEnvironment(
  environmentId: string,
  runtimes: readonly CmmdRuntimeInventoryRuntime[],
): RuntimeAvailabilityForEnvironment {
  const runtime = findInventoryRuntimeForEnvironment(environmentId, runtimes);
  return runtime ? runtimeAvailability(runtime) : "unknown";
}

/** Snapshot read for click handlers; render paths subscribe to the inventory resource instead. */
export function readRuntimeAvailabilityForEnvironment(
  environmentId: string,
): RuntimeAvailabilityForEnvironment {
  return resolveRuntimeAvailabilityForEnvironment(
    environmentId,
    cmmdRuntimeInventoryResource.read().inventory?.runtimes ?? [],
  );
}
